import React from 'react';
import { ShipmentEvent } from '@wove/api';
import { Card, CardContent, Badge, Spinner } from '@wove/web-ui';
import { format } from 'date-fns';
import { 
  MapPin, 
  Calendar, 
  Package, 
  Truck, 
  Ship, 
  Plane, 
  FileText,
  Clock,
} from 'lucide-react';

interface EventsTabProps {
  events: ShipmentEvent[];
  isLoading: boolean;
}

const EventsTab: React.FC<EventsTabProps> = ({ events, isLoading }) => {
  const getEventIcon = (eventType: string) => {
    const icons: Record<string, React.ReactNode> = {
      created: <FileText size={16} />,
      departed: <Ship size={16} />,
      arrived: <MapPin size={16} />,
      delivered: <Package size={16} />,
      pickup: <Truck size={16} />,
      customs_cleared: <FileText size={16} />,
      in_transit: <Plane size={16} />,
      delayed: <Clock size={16} />,
    };
    return icons[eventType] || <Package size={16} />;
  };

  const getEventVariant = (eventType: string): "default" | "secondary" | "success" | "warning" | "error" | "info" => {
    const variants: Record<string, "default" | "secondary" | "success" | "warning" | "error" | "info"> = {
      created: 'secondary',
      departed: 'info',
      arrived: 'success',
      delivered: 'success',
      pickup: 'warning',
      customs_cleared: 'default',
      in_transit: 'info',
      delayed: 'error',
    };
    return variants[eventType] || 'secondary';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Clock className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-500">No events recorded yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-5 top-8 bottom-8 w-0.5 bg-gray-200"></div>

      {/* Events */}
      <div className="space-y-4">
        {events.map((event, index) => (
          <div key={event.id} className="relative flex items-start gap-4">
            {/* Timeline dot */}
            <div className="relative z-10 flex items-center justify-center w-10 h-10 bg-white border-2 border-gray-300 rounded-full">
              {getEventIcon(event.eventType)}
            </div>

            {/* Event card */}
            <Card className="flex-1">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">
                        {event.eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </h4>
                      <Badge variant={getEventVariant(event.eventType)}>
                        {event.eventType}
                      </Badge>
                    </div>
                    
                    {event.eventDescription && (
                      <p className="text-sm text-gray-600 mb-2">{event.eventDescription}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>{format(new Date(event.eventDate), 'MMM d, yyyy h:mm a')}</span>
                      </div>
                      
                      {event.eventLocation && (
                        <div className="flex items-center gap-1">
                          <MapPin size={14} />
                          <span>{event.eventLocation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventsTab;